<template>
  <div>
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="addressBookDetailForm">
        <b-form ref="addressBookDetailFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Address Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Address Type')"
                  rules="required"
                >
                  <b-form-select
                    id="type"
                    v-model="values.type"
                    name="type"
                    tabindex="2"
                    :options="addressTypeOptions"
                    class="mb-0"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Company Name') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Company Name')"
                  rules="required"
                >
                  <b-form-input
                    id="name"
                    v-model="values.name"
                    name="name"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Company Name')"
                    tabindex="1"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Attention Name') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Attention Name')"
                  rules="required"
                >
                  <b-form-input
                    id="attention_name"
                    v-model="values.attention_name"
                    name="attention_name"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Attention Name')"
                    tabindex="3"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6" lg="4">
              <validation-provider
                #default="{ errors }"
                :name="t('Country')"
                rules="required"
                vid="country_id"
              >
                <b-form-group label-for="country_id" :state="errors.length > 0 ? false : null">
                  <div class="d-flex form-group-label">
                    <label for="name">{{ t('Country') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <v-select
                    id="country_id"
                    v-model="values.country_id"
                    :options="countries"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="4"
                    @input="getStateList"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group label-for="postal_code_id" :label="t('Postal Code')">
                <validation-provider
                  #default="{ errors }"
                  :name="t('Postal Code')"
                  rules="required"
                  vid="postal_code_id"
                  ref="postalCodeIdValidation"
                >
                  <v-select
                    :disabled="!values.country_id"
                    v-model="values.postal_code_id"
                    :options="postalCodeOptions"
                    :placeholder="t('Enter Postal Code')"
                    label="combinedLabel"
                    @search="handlePostalCodeSearch"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6" lg="4">
              <validation-provider
                #default="{ errors }"
                ref="stateValidation"
                :name="t('City')"
                rules="required"
                vid="state_id"
              >
                <b-form-group label-for="state_id" :state="errors.length > 0 ? false : null">
                  <div class="d-flex form-group-label">
                    <label for="name">{{ t('City') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <v-select
                    id="state_id"
                    v-model="values.state_id"
                    :options="states"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="5"
                    @input="getCityList"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6" lg="4">
              <validation-provider
                #default="{ errors }"
                ref="cityValidation"
                :name="t('District')"
                rules="required"
                vid="city_id"
              >
                <b-form-group label-for="city_id" :state="errors.length > 0 ? false : null">
                  <div class="d-flex form-group-label">
                    <label for="name">{{ t('District') }}</label>
                    <span class="text-danger">*</span>
                  </div>
                  <v-select
                    id="city_id"
                    v-model="values.city_id"
                    :options="cities"
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="6"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="phone_number">{{ t('Phone Number') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Phone Number')"
                  rules="required"
                >
                  <b-form-input
                    id="phone_number"
                    v-model="values.phone_number"
                    name="phone_number"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Phone Number')"
                    tabindex="7"
                    type="number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="6" lg="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="email">{{ t('Email') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Email')"
                  rules="required|email"
                >
                  <b-form-input
                    id="email"
                    v-model="values.email"
                    name="email"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Email')"
                    tabindex="8"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="postal_code_id">{{ t('Address') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Address')" rules="required">
                  <b-form-textarea
                    id="textarea"
                    v-model="values.address"
                    :placeholder="t('Address')"
                    rows="3"
                    no-resize
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-checkbox v-model="values.status" name="check-button" switch tabindex="10">
                {{ values.status ? t('Active') : t('Passive') }}
              </b-form-checkbox>

              <b-button
                variant="primary"
                class="mt-1 d-flex align-items-center"
                type="submit"
                tabindex="11"
              >
                {{
                  submitting
                    ? t('Please Wait')
                    : type === 'new'
                    ? t('Create Address Book')
                    : t('Edit Address Book')
                }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>
<script>
import {
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BFormSelect,
  BFormInvalidFeedback,
  BFormCheckbox,
  BFormTextarea,
  BButton,
  BOverlay
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import vSelect from 'vue-select';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';

export default {
  components: {
    BOverlay,
    BButton,
    BFormInvalidFeedback,
    vSelect,
    BFormInput,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    BFormSelect,
    BFormCheckbox,
    BFormTextarea,

    ValidationObserver,
    ValidationProvider
  },
  mixins: [helper],
  props: {
    type: {
      type: String,
      default: 'new'
    },
    submitting: {
      type: Boolean,
      default: false
    },
    values: {
      type: Object,
      default() {
        return {
          name: '',
          type: '',
          attention_name: '',
          country_id: '',
          state_id: '',
          city_id: '',
          phone_number: '',
          email: '',
          postal_code_id: '',
          status: true
        };
      }
    }
  },
  data() {
    return {
      addressType: [],
      packageStatus: [],
      postalCodes: [],
      filteredPostalCodes: [],
      minimumPostalCodeLength: 3
    };
  },
  computed: {
    ...mapGetters({
      countries: 'app/countries',
      states: 'app/consignorStates',
      cities: 'app/consignorCities'
    }),

    postalCodeOptions() {
      return this.mapToOptions(this.filteredPostalCodes);
    },

    addressTypeOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.addressType && this.addressType.length > 0) {
        options.push(
          ...this.addressType.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    },

    packageStatusTypeOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.packageStatus && this.packageStatus.length > 0) {
        options.push(
          ...this.packageStatus.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    }
  },
  async created() {
    await this.$store.dispatch('app/getCountries');
    this.addressType = await this.getAddressType();
    this.packageStatus = await this.getPackageStatus();
  },
  methods: {
    submit() {
      this.$refs.addressBookDetailForm.validate().then(success => {
        if (success) {
          this.$emit('submit', {
            ...this.values,
            country_id: this.values.country_id.value,
            state_id: this.values.state_id.value,
            city_id: this.values.city_id.value,
            postal_code_id: this.values.postal_code_id.value
          });
        }
      });
    },
    getStateList(data) {
      this.values.state_id = '';
      this.$refs.stateValidation.reset();
      this.getCityList('');
      this.values.postal_code_id = '';
      this.$refs.postalCodeIdValidation.reset();
      this.postalCodes = [];
      this.filteredPostalCodes = [];

      if (data && data.value) {
        this.$store.dispatch('app/getConsignorStates', data.value);
      }
    },
    getCityList(data) {
      this.values.city_id = '';
      this.$refs.cityValidation.reset();

      if (data && data.value) {
        this.$store.dispatch('app/getConsignorCities', {
          countryId: this.values.country_id.value,
          cityId: data.value
        });
      }
    },
    resetForm() {
      this.$refs.addressBookDetailFormElement.reset();
    },
    mapToOptions(list) {
      return list.map(code => ({
        value: code.id,
        combinedLabel: `${code.zipcode} - ${code.place}`,
        state: code.state,
        city: code.city
      }));
    },
    handlePostalCodeSearch(postalCode) {
      if (postalCode.length >= this.minimumPostalCodeLength) {
        this.filterPostalCodes(postalCode);
      }
    },
    filterPostalCodes(postalCode) {
      const data = {
        country_code: this.values.country_id.iso2,
        zipcode: postalCode
      };

      useService.autoPostalCodes(data).then(response => {
        this.postalCodes = response.data;
        const filtered = this.postalCodes.filter(code => code.zipcode.includes(postalCode));

        this.filteredPostalCodes = filtered.map(code => ({
          place: code.place,
          zipcode: code.zipcode,
          state: code.state,
          city: code.province,
          id: code.id
        }));
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
